import React, { useEffect, useRef } from "react";
import { classNames } from "primereact/utils";
import { SpoofyCrudPage } from "../Crud/SpoofyCrudPageablePage";
import {
	ConversationService,
	IConversationChatMessageModel, IConversationMessageMetadataModel,
	IConversationMessageModel,
	IConversationModel,
} from "../../Services/ConversationService";
import "./Conversation.scss"
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import ConversationBubble from "./ConversationBubble";
import { InputTextarea } from "primereact/inputtextarea";
import { toast } from "react-toastify";
import secureLocalStorage from "react-secure-storage";
import SecurityUserResponse from "../../Models/Authentication/SecurityUserResponse";
import { format, parseISO } from "date-fns";
import { InputSwitch } from "primereact/inputswitch";
import { v4 as uuidv4 } from 'uuid';
import ReferenceModel from "../../Models/ReferenceModel";
import { ConversationTypeService } from "../../Services/ConversationTypeService";
import { Tooltip } from "primereact/tooltip";
import { AutoComplete, AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { confirmPopup } from "primereact/confirmpopup";
import MultiCustomerCheck from "../MultiCustomerCheck";
import { CustomerService } from "../../Services/customerService";
import { DashlyDropdown, DashlyFilterDropdown, DashlyFilterSearch } from "../inputs";
import WorkflowCustomerTag from "../Custom/WorkflowCustomerTag";
import { IChartJsFilterModel } from "../../Services/DashboardItemService";
import { getStoredUserCustomerUuid } from "../../Util/secureStorageSession";
import { exportConversationToPdf, isConversationPdfExportable } from "./conversationPdfExport";
import { DASHLY_LAYOUT_NARROW_MEDIA } from "../../Util/layoutBreakpoints";
import {
	CONVERSATION_HISTORY_PAGE_SIZE,
	CONVERSATION_HISTORY_PREFETCH_ROWS,
	conversationTitleFromMessage,
	resolveAskAiComposerCustomer,
	setStoredAskAiCustomerUuid,
} from "./conversationAskAiUtils";

export const CONVERSATION_HISTORY_TRAY_ID = "conversation-history-tray";

interface ConversationProps {
	type: "MESSAGING" | "ASKAI" | undefined,
	fullscreen: boolean,
	embedded?: boolean,
	minMessageSize?: number,
	groupByDate?: boolean,
	setVisible?: (value: (((prevState: boolean) => boolean) | boolean)) => void,
	selectionAllowed?: boolean,
	location: string,
	locationIdentifier?: string,
	/** Applied dashboard chart filters — sent with Ask AI messages for SETUP query grounding. */
	dashboardFilters?: IChartJsFilterModel,
	/** When set, create a conversation and send this question as soon as the panel is ready. */
	initialQuestion?: string,
}

const Conversation: React.FC<ConversationProps> = ({
	type = "MESSAGING",
	fullscreen = false,
	embedded = false,
	minMessageSize = 10,
	groupByDate = false,
	setVisible,
	selectionAllowed = true,
	location,
	locationIdentifier,
	dashboardFilters,
	initialQuestion,
}: ConversationProps) => {

	const { t } = useTranslation();

	const userAuth: SecurityUserResponse = JSON.parse(secureLocalStorage.getItem('user') as string);

	const chatBottomRef = useRef<HTMLDivElement | null>(null);
	const filterRef = useRef<HTMLDivElement | null>(null);
	const chatContentRef = useRef<HTMLDivElement | null>(null);
	const skipMessageReloadForUuidRef = useRef<string | null>(null);

	const [conversationTypeSelectList, setConversationTypeSelectList] = React.useState<ReferenceModel[]>();
	const [selectedConversation, setSelectedConversation] = React.useState<IConversationModel>();
	const [selectedConversationMessages, setSelectedConversationMessages] = React.useState<IConversationMessageModel[]>([]);
	const isSidebarAskAi = Boolean(fullscreen && embedded);
	const [chatDisabled, setChatDisabled] = React.useState<boolean>(!isSidebarAskAi && fullscreen);
	const [filterVisible, setFilterVisible] = React.useState<boolean>(false);
	const [filterParams, setFilterParams] = React.useState<any>({});

	const [subjectEditMode, setSubjectEditMode] = React.useState<boolean>(false);
	const [exportingPdf, setExportingPdf] = React.useState<boolean>(false);
	const [subject, setSubject] = React.useState<string>(selectedConversation?.title ?? "");
	const [autoCompleteSubjects, setAutoCompleteSubjects] = React.useState<string[]>([]);
	const [refreshTrigger, setRefreshTrigger] = React.useState<number>(0);

	// const [chatMessage, setChatMessage] = React.useState<IConversationChatMessageModel>();
	const [chatMessage, setChatMessage] = React.useState<string>("");
	const [isSelectedConversation, setIsSelectedConversation] = React.useState<boolean>(false);

	const [isMultiCustomer] = React.useState<boolean>(() => MultiCustomerCheck() as boolean);
	const isKnowledgeBaseAskAi = location === "KNOWLEDGE_BASE";
	const needsCustomerForAskAi = isMultiCustomer && type === "ASKAI" && location === "ASKAI";
	const startedInitialQuestion = useRef(false);
	const [customerSelectItems, setCustomerSelectItems] = React.useState<ReferenceModel[]>([]);
	const [newAskAiCustomer, setNewAskAiCustomer] = React.useState<ReferenceModel | null>(null);
	const [composerCustomerMissing, setComposerCustomerMissing] = React.useState(false);
	const [historyTrayOpen, setHistoryTrayOpen] = React.useState(false);
	const composerCustomerRef = useRef<HTMLDivElement | null>(null);
	const composerInputWrapRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		initializeSelectItems();
	}, []);

	useEffect(() => {
		if (!isSidebarAskAi) {
			return;
		}
		const media = window.matchMedia(DASHLY_LAYOUT_NARROW_MEDIA);
		const closeWhenWide = () => {
			if (!media.matches) {
				setHistoryTrayOpen(false);
			}
		};
		closeWhenWide();
		media.addEventListener("change", closeWhenWide);
		return () => media.removeEventListener("change", closeWhenWide);
	}, [isSidebarAskAi]);

	useEffect(() => {
		if (!historyTrayOpen) {
			return;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setHistoryTrayOpen(false);
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [historyTrayOpen]);

	useEffect(() => {
		if (!needsCustomerForAskAi) {
			return;
		}

		new CustomerService().fetchSelectItems().then((response) => {
			setCustomerSelectItems(response?.data?.data ?? []);
		}).catch(() => {
			setCustomerSelectItems([]);
		});
	}, [needsCustomerForAskAi]);

	useEffect(() => {
		if (!needsCustomerForAskAi || customerSelectItems.length === 0 || newAskAiCustomer) {
			return;
		}
		const resolved = resolveAskAiComposerCustomer(customerSelectItems, {
			filterCustomerUuid: filterParams.customer,
			conversationCustomer: selectedConversation?.customer,
		});
		if (resolved) {
			setNewAskAiCustomer(resolved);
			setStoredAskAiCustomerUuid(resolved.uuid ?? null);
		}
	}, [needsCustomerForAskAi, customerSelectItems]);

	useEffect(() => {
		const conversationUuid = selectedConversation?.uuid;
		if (!conversationUuid) {
			setSelectedConversationMessages([]);
			return;
		}
		if (skipMessageReloadForUuidRef.current === conversationUuid) {
			skipMessageReloadForUuidRef.current = null;
			return;
		}
		setSelectedConversationMessages([]);
		loadConversationMessages(selectedConversation);
	}, [selectedConversation?.uuid]);

	useEffect(() => {
		if (!selectedConversation) {
			return;
		}

		loadConversationMessages(selectedConversation);
	}, [filterParams]);

	const initializeSelectItems = () => {
		new ConversationTypeService().fetchSelectItems().then(response => {
			setConversationTypeSelectList(response.data.data)
		})
	}

	const loadConversationMessages = (conversation: IConversationModel) => {
		new ConversationService().fetchMessages(conversation?.uuid ?? '', filterParams).then((response) => {
			setSelectedConversationMessages(response.data.data ?? []);
		}).catch((ex) => {
			toast.error(t('common.load.failed'))
		})
	}

	useEffect(() => {
		scrollToBottom();
	}, [selectedConversationMessages]);

	const scrollToBottom = () => {
		if (fullscreen && chatContentRef.current) {
			// In fullscreen mode, scroll the chat content container directly
			chatContentRef.current.scrollTo({
				top: chatContentRef.current.scrollHeight,
				behavior: "smooth"
			});
		} else if (chatBottomRef.current) {
			// In non-fullscreen mode, use scrollIntoView (will scroll the nearest scrollable ancestor)
			chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}

	const scrollToFilter = () => {
		filterRef.current?.scrollIntoView({ behavior: "smooth" });
	}

	const titleTemplate = (rowdata: IConversationModel) => (
		<div className="conversation-list-item">
			<div className="conversation-list-item__body">
				<span className="conversation-list-item__title">{rowdata.title}</span>
				<WorkflowCustomerTag customer={rowdata.customer} isMultiCustomer={isMultiCustomer} />
			</div>
			<Button
				icon="pi pi-trash"
				text
				onClick={(event) => {
					event.stopPropagation();
					handleDelete(rowdata, event);
				}}
				tooltip={t("common.action.delete")}
				tooltipOptions={{ position: "bottom" }}
			/>
		</div>
	);

	const tableColumns = () => [
		<Column field="title" header={t('common.title')} body={titleTemplate} />,
	];

	const onRowSelectionChange = (selection: any) => {
		setChatDisabled(false);
		setSelectedConversation(selection);
		setFilterVisible(false);
		setIsSelectedConversation(true);
		setHistoryTrayOpen(false);
		setSubjectEditMode(false);
		if (selection?.customer?.uuid) {
			setNewAskAiCustomer(selection.customer);
			setStoredAskAiCustomerUuid(selection.customer.uuid);
			setComposerCustomerMissing(false);
		}
	}

	const onGoBackAction = () => {
		setFilterVisible(false);
		setSubjectEditMode(false);
		setIsSelectedConversation(false);
		setSelectedConversation(undefined);
		setSelectedConversationMessages([]);
	}

	const resolveDefaultAskAiCustomer = (): ReferenceModel | null => {
		return resolveAskAiComposerCustomer(customerSelectItems, {
			filterCustomerUuid: filterParams.customer,
			conversationCustomer: selectedConversation?.customer,
		}) ?? newAskAiCustomer;
	};

	const focusComposerInput = () => {
		window.setTimeout(() => {
			composerInputWrapRef.current?.querySelector("textarea")?.focus();
		}, 0);
	};

	const onAddConversationAction = async () => {
		setHistoryTrayOpen(false);
		setChatDisabled(false);
		setSelectedConversation(undefined);
		setSelectedConversationMessages([]);
		setIsSelectedConversation(false);
		setComposerCustomerMissing(false);
		setSubjectEditMode(false);
		if (needsCustomerForAskAi && !newAskAiCustomer) {
			const defaultCustomer = resolveDefaultAskAiCustomer();
			if (defaultCustomer) {
				setNewAskAiCustomer(defaultCustomer);
				setStoredAskAiCustomerUuid(defaultCustomer.uuid ?? null);
			}
		}
		focusComposerInput();
	}

	const applyConversationTitle = (conversationUuid: string, title: string) => {
		setSelectedConversation((current) => {
			if (!current || current.uuid !== conversationUuid) {
				return current;
			}
			return { ...current, title };
		});
		setRefreshTrigger((previous) => previous + 1);
	};

	const onSubjectEditAction = () => {
		setFilterVisible(false);
		if (!selectedConversation) {
			return;
		}

		if (subjectEditMode) {
			const nextTitle = `${subject ?? ""}`.trim();
			if (!nextTitle || nextTitle === (selectedConversation.title ?? "").trim()) {
				setSubjectEditMode(false);
				return;
			}

			const loadingToastId = toast.loading(t("common.saving"));
			new ConversationService().putItem(selectedConversation.uuid, {
				...selectedConversation,
				title: nextTitle,
			}).then(() => {
				applyConversationTitle(selectedConversation.uuid, nextTitle);
				toast.update(loadingToastId, { type: "success", render: t("common.saved"), isLoading: false, autoClose: 1500 });
			}).catch(() => {
				toast.update(loadingToastId, { type: "error", render: t("common.failed"), isLoading: false, autoClose: 1500 });
			}).finally(() => {
				setSubjectEditMode(false);
			});
			return;
		}

		setSubject(selectedConversation.title ?? "");
		setSubjectEditMode(true);
	};

	const onSubjectCompleteMethod = (event: AutoCompleteCompleteEvent) => {
		new ConversationService().fetchSubjectSearch(event.query.toLowerCase()).then((response) => {
			setAutoCompleteSubjects(response.data.data)
		}).catch(() => {
			toast.error(t('common.failed'));
		})
	}

	const noMessagesContent = () => (
		<div className="h-full w-full flex justify-content-center align-items-center">
			<div className="text-center p-4">
				<i className="pi pi-exclamation-triangle text-xl mb-4" />
				<div>
					{t('conversation.no-messages')}
				</div>
			</div>
		</div>
	);

	const noConversationContent = () => (
		<div className="h-full w-full flex justify-content-center align-items-center">
			<div className="conversation-landing text-center p-4">
				{isSidebarAskAi ? (
					<>
						<div className="conversation-landing__title">{t("ask-ai")}</div>
						<p className="conversation-landing__subtitle">{t("ask-ai.subtitle")}</p>
					</>
				) : (
					<>
						<i className="pi pi-times-circle text-xl mb-4" />
						<div>
							{t("conversation.no-selected")}
						</div>
					</>
				)}
			</div>
		</div>
	);

	const changeFilterParameter = (key: string, value: any) => {
		setFilterParams((prev: any) => {
			const next = { ...prev };
			if (value === undefined || value === null || value === '') {
				delete next[key];
			} else {
				next[key] = value;
			}
			return next;
		});
	}

	const confirmDelete = (event: any, rowdata: any) => {
		confirmPopup({
			target: event.currentTarget,
			message: t("common.action.delete.confirm"),
			icon: 'pi pi-info-circle',
			defaultFocus: 'reject',
			acceptClassName: 'p-button-danger',
			accept: () => {
				new ConversationService().deleteItem(rowdata.uuid).then(() => {
					toast.success(t("common.deleted"));
					if (selectedConversation?.uuid === rowdata.uuid) {
						setIsSelectedConversation(false);
						setSelectedConversation(undefined);
						setSelectedConversationMessages([]);
					}
					setRefreshTrigger(prev => prev + 1);
				}).catch(() => {
					toast.error(t("common.failed"));
				});
			},

		});
	};

	const handleDelete = (rowdata: any, event: any) => {
		confirmDelete(event, rowdata);
	}


	const toggleFilterVisible = () => {
		setFilterVisible(!filterVisible);

		if (!filterVisible) {
			scrollToFilter();
		}
	}

	const selectedCustomerFilter = customerSelectItems.find(
		(customer) => customer.uuid === filterParams.customer,
	) ?? null;

	const sidebarListFilters = () => (
		<>
			{needsCustomerForAskAi && (
				<DashlyFilterDropdown
					inputId="conversation-filter-customer"
					value={selectedCustomerFilter}
					options={customerSelectItems}
					onChange={(event) => changeFilterParameter('customer', event.value?.uuid)}
					optionLabel="name"
					placeholder={t('user.customer')}
					filter
					className="w-full"
					controlSize="sm"
				/>
			)}
			<DashlyFilterSearch
				inputId="conversation-filter-title"
				value={filterParams.title || ''}
				onChange={(value) => changeFilterParameter('title', value)}
				placeholder={t('common.search')}
				className="w-full"
				controlSize="sm"
			/>
		</>
	);

	const filterContent = () => {
		if (!isSelectedConversation) {
			return (<>
				<div className="conversation-sidebar-filters">
					{sidebarListFilters()}
				</div>
			</>);
		}

		if (type === "ASKAI") {
			return (<>
				<div className="flex align-items-center">
					<InputSwitch checked={filterParams['includeAIThinking'] ?? false}
						onChange={(event) => changeFilterParameter('includeAIThinking', event.value)} />
					<span className="ml-2">
						{t('conversation.filter.include-ai-thinking')}
					</span>
				</div>
			</>);
		}
		else if (type === "MESSAGING") {
			return (<>

			</>);
		}
		return undefined;
	}

	const onChatChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setChatMessage(event.target?.value ?? "");
	};

	const determineChatHeader = () => {
		if (subjectEditMode) {
			return (
				<AutoComplete autoFocus value={subject}
					suggestions={autoCompleteSubjects}
					completeMethod={onSubjectCompleteMethod}
					panelStyle={{
						width: '0px'
					}}
					delay={200}
					onChange={(event) => {
						setSubject(event.value);
					}}
				/>
			)
		} else {
			if (type === "ASKAI") {
				if (selectedConversation) {
					return selectedConversation.title ? selectedConversation.title : '';
				}
				return !fullscreen ? t('conversation.ask-ai') : "";
			} else if (type === "MESSAGING") {
				return t('conversation.chat');
			} else {
				return t('conversation.unknown');
			}
		}
	}

	const insertSpoofedMessageInChat = (
		timeout: number,
		chatMessage: IConversationMessageModel
	): Promise<void> => {
		return new Promise((resolve) => {
			chatMessage.uuid === "PLACEHOLDER:" + uuidv4();

			setSelectedConversationMessages((prevState: IConversationMessageModel[]) => [
				...prevState,
				{
					uuid: "SENDING",
					conversationMessageType: chatMessage.conversationMessageType,
					messageSentTs: chatMessage.messageSentTs
				} as IConversationMessageModel,
			]);

			setTimeout(() => {
				setSelectedConversationMessages((prevState: IConversationMessageModel[]) => [
					...prevState.filter((conv) => conv.uuid !== "SENDING"),
					chatMessage,
				]);
				resolve(); // tell the awaiting sailor we're done
			}, timeout);
		});
	};

	const resolveCustomerByUuid = (customerUuid?: string | null): ReferenceModel | null => {
		if (newAskAiCustomer && newAskAiCustomer.uuid === customerUuid && newAskAiCustomer.name) {
			return newAskAiCustomer;
		}
		if (customerUuid) {
			const fromSelect = customerSelectItems.find((customer) => customer.uuid === customerUuid);
			if (fromSelect) {
				return fromSelect;
			}
		}
		if (newAskAiCustomer?.name) {
			return newAskAiCustomer;
		}
		return customerUuid ? { uuid: customerUuid } as ReferenceModel : null;
	};

	const generateConversationBeforeChat = async (customerUuid?: string, titleSource?: string): Promise<IConversationModel> => {
		const resolvedCustomerUuid = isKnowledgeBaseAskAi ? getStoredUserCustomerUuid() : customerUuid;
		const resolvedTitle = titleSource?.trim() ? conversationTitleFromMessage(titleSource) : "";
		return await new ConversationService().postItem({
			title: resolvedTitle,
			conversationType: conversationTypeSelectList?.find((ct) => ct.code === type),
			location: location,
			locationIdentifier: locationIdentifier,
			...(resolvedCustomerUuid ? { customer: { uuid: resolvedCustomerUuid } as ReferenceModel } : {}),
		} as IConversationModel).then((response) => {
			const conversation = {
				uuid: response.data.data,
				title: resolvedTitle,
				conversationType: conversationTypeSelectList?.find((conversationType) => conversationType.code === type),
				location,
				locationIdentifier,
				active: true,
				...(resolvedCustomerUuid ? { customer: { uuid: resolvedCustomerUuid } as ReferenceModel } : {}),
			} as IConversationModel;
			skipMessageReloadForUuidRef.current = conversation.uuid;
			setSelectedConversation(conversation);
			return conversation;
		});
	}

	const submitConversationMessage = async (conversation: IConversationModel, message: string) => {
		setChatDisabled(type === "ASKAI");

		if (type === "ASKAI") {
			await insertSpoofedMessageInChat(400, {
				conversationMessageType: { code: "ASKAIQSTN" },
				createdBy: { name: userAuth.firstName + " " + userAuth.surname },
				message: message,
				messageSentTs: new Date().toISOString()
			} as IConversationMessageModel);
		}

		const payload: IConversationChatMessageModel = { message: message };
		if (dashboardFilters && Object.keys(dashboardFilters).length > 0) {
			payload.dashboardFilters = dashboardFilters;
		}
		if (!conversation.title?.trim()) {
			applyConversationTitle(conversation.uuid, conversationTitleFromMessage(message));
		}

		new ConversationService().postMessage(conversation?.uuid, payload).then(() => {
			setChatMessage("");
		}).catch(() => {
			toast.error(t('common.failed'));
		}).finally(() => {
			new ConversationService().fetchItem(conversation.uuid).then((response) => {
				const loaded = response.data.data;
				if (loaded) {
					setSelectedConversation((current) => {
						if (!current || current.uuid !== conversation.uuid) {
							return current;
						}
						return {
							...current,
							...loaded,
							customer: loaded.customer?.name
								? loaded.customer
								: resolveCustomerByUuid(loaded.customer?.uuid ?? current.customer?.uuid) ?? current.customer,
						};
					});
					setRefreshTrigger((previous) => previous + 1);
				} else {
					setRefreshTrigger((previous) => previous + 1);
				}
			}).catch(() => {
				setRefreshTrigger((previous) => previous + 1);
			});

			loadConversationMessages(conversation);
			setChatDisabled(false);
		})

		if (type === "ASKAI") {
			setTimeout(() => {
				insertSpoofedMessageInChat(250, {
					conversationMessageType: { code: "ASKAIGNRN" },
					message: message,
					messageSentTs: new Date().toISOString()
				} as IConversationMessageModel)
			}, 300);
		}
	}

	const focusComposerCustomer = () => {
		setComposerCustomerMissing(true);
		composerCustomerRef.current?.querySelector<HTMLElement>("input, .p-dropdown")?.focus();
	};

	const onChatEnterClicked = (event: any) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			setIsSelectedConversation(true);
			event.preventDefault(); // stop newline
			onChatSubmit(event); // yer custom function
		}
	};


	const onChatSubmit = async (event: any) => {
		let conversation = selectedConversation;

		if (!chatMessage || chatMessage.length < minMessageSize) {
			toast.error(t("conversation.message.too-short", { minMessageSize: minMessageSize }));
			return;
		}

		if (!conversation && needsCustomerForAskAi) {
			const customer = newAskAiCustomer ?? resolveDefaultAskAiCustomer();
			if (!customer?.uuid) {
				toast.error(t("conversation.ask-ai.customer-required"));
				focusComposerCustomer();
				return;
			}
			setNewAskAiCustomer(customer);
			setStoredAskAiCustomerUuid(customer.uuid);
			setComposerCustomerMissing(false);
			conversation = await generateConversationBeforeChat(customer.uuid, chatMessage);
			setRefreshTrigger((prev) => prev + 1);
		}

		if (!conversation) {
			conversation = await generateConversationBeforeChat(undefined, chatMessage);
		}

		if (conversation) {
			setIsSelectedConversation(true);
			await submitConversationMessage(conversation, chatMessage);
		}
	}

	const groupMessagesByDay = (messages: IConversationMessageModel[]) => {
		return messages.reduce((acc: Record<string, IConversationMessageModel[]>, msg) => {
			// Parse & format date to day string (e.g., 2025-08-21)
			const day = format(parseISO(msg.messageSentTs), "dd-MM-yyyy");
			if (!acc[day]) acc[day] = [];
			acc[day].push(msg);
			return acc;
		}, {});
	};

	const conversationTable = () => {
		return (<>
			<div className={classNames(
				"conversation-table",
				fullscreen ? "col-3" : "col-12",
				needsCustomerForAskAi ? "conversation-table--multi-customer" : "",
			)}>

				{fullscreen && (<div className="add-conversation">
					<Button
						className="add-conversation-button primary-btn"
						icon="pi pi-plus"
						onClick={() => onAddConversationAction()}
					>
						{t("conversation.new-question")}
					</Button>
				</div>)}

				{fullscreen && (
					<div className="conversation-sidebar-filters conversation-sidebar-filters--inline">
						{sidebarListFilters()}
					</div>
				)}

				<SpoofyCrudPage
					key={JSON.stringify(filterParams) + refreshTrigger}
					properties={{
						type: "datatable",
						lazy: true,
						paginator: {
							enabled: true,
							initialOption: CONVERSATION_HISTORY_PAGE_SIZE,
							infiniteScroll: true,
							infiniteScrollOffsetRows: CONVERSATION_HISTORY_PREFETCH_ROWS,
						},
						rowSelection: {
							enabled: true,
							selectionMode: "single",
							onChange: onRowSelectionChange
						},
						defaultCrudActions: {
							deleteAction: false,
							editAction: false,
							viewAction: false,
							addAction: false
						},
						columns: tableColumns()
					}}
					crudDialog={{
						enabled: false,
					}}
					pageTitle={"conversation"}
					showPageHeader={false}
					setNavigationTitle={false}
					service={new ConversationService()}
					filter={{
						enabled: false,
						default: false,
						initial: {
							'location': location,
							'locationIdentifier': locationIdentifier,
							...filterParams
						}
					}}
				/>
			</div>
		</>)
	};

	const onConversationMessageMetadataUpdate = (conversationMessageUuid: string, metadata: IConversationMessageMetadataModel[] | undefined) => {
		setSelectedConversationMessages((prevState) =>
			prevState.map((message) => {
				if (message.uuid === conversationMessageUuid) {
					return {
						...message,
						metadata: metadata
					};
				}
				return message;
			})
		);
	}

	const conversationMessageContent = () => {
		return (<>
			{!selectedConversationMessages && (
				noMessagesContent()
			)}

			{selectedConversationMessages && (
				<>
					{!groupByDate && (
						selectedConversationMessages.map((item: IConversationMessageModel) => (
							<ConversationBubble
								key={item.uuid}
								type={type}
								conversationMessage={item}
								user={userAuth}
								selectionAllowed={selectionAllowed}
							/>
						)))
					}

					{groupByDate &&
						(Object.entries(groupMessagesByDay(selectedConversationMessages)).map(([day, messages]) => (
							<div key={day}>
								<ConversationBubble
									type="DATE"
									conversationMessage={{
										messageSentTs: day,
									} as IConversationMessageModel}
									selectionAllowed={false}
									user={userAuth}
								/>

								{messages.map((item: IConversationMessageModel) => (
									<ConversationBubble
										key={item.uuid}
										type={type}
										groupByDate={groupByDate}
										conversationMessage={item}
										selectionAllowed={selectionAllowed}
										onMetadataUpdate={onConversationMessageMetadataUpdate}
										user={userAuth}
									/>
								))}
							</div>
						)))
					}
				</>
			)}
		</>)
	};

	const canExportConversationPdf = selectedConversation
		&& selectedConversationMessages.some(isConversationPdfExportable);

	const onExportConversationPdf = async () => {
		if (!selectedConversation || exportingPdf || !canExportConversationPdf) {
			return;
		}
		setExportingPdf(true);
		try {
			await exportConversationToPdf({
				conversation: selectedConversation,
				messages: selectedConversationMessages,
				type,
				t,
			});
			toast.success(t('conversation.exportPdfSuccess'));
		} catch {
			toast.error(t('conversation.exportPdfError'));
		} finally {
			setExportingPdf(false);
		}
	};

	const exportPdfButton = selectedConversation ? (
		<Button
			icon={exportingPdf ? "pi pi-spinner pi-spin" : "pi pi-file-pdf"}
			text
			className="conversation-header-btn export-pdf-icon"
			disabled={exportingPdf || !canExportConversationPdf}
			onClick={() => { void onExportConversationPdf(); }}
			aria-label={t('conversation.exportPdf')}
			data-pr-tooltip={t('conversation.exportPdf')}
		/>
	) : undefined;

	const conversationHeader = () => {

		if (fullscreen) {
			return <>
				<div className="left-slot col-3">
					<div className="flex-grow-1 conversation-list-header fullscreen">
						<div className="conversation-list-header-row">
							<Tooltip target="[data-pr-tooltip]" showDelay={700} />
							<div className="title"
								data-pr-tooltip={subjectEditMode !== undefined ? undefined : determineChatHeader().toString()}
								data-pr-position="left">
								{t('conversation.ask-ai')}
							</div>
						</div>
					</div>
				</div>

				<div className="right-slot col-9">
					<div className="flex-grow-1 conversation-list-header fullscreen">
						<div className="conversation-list-header-row">
							<Tooltip target="[data-pr-tooltip]" showDelay={700} />
							<div className="title"
								data-pr-tooltip={subjectEditMode !== undefined ? undefined : determineChatHeader().toString()}
								data-pr-position="left">
								{determineChatHeader()}
							</div>

							{exportPdfButton}
							{selectedConversation && (
								<Button
									icon={subjectEditMode ? "pi pi-save" : "pi pi-pencil"}
									text
									className="conversation-header-btn edit-icon"
									onClick={() => onSubjectEditAction()}
								/>
							)}
						</div>
					</div>
				</div>
			</>
		}


		return (<>
			<div className="left-slot">
				{setVisible && !fullscreen && selectedConversation && (
					<Button
						icon="pi pi-arrow-left"
						text
						className="conversation-header-btn back-icon"
						onClick={() => onGoBackAction()}
					/>
				)}
			</div>

			<div className="center-slot">
				{(setVisible) && (
					<>
						<Tooltip target="[data-pr-tooltip]" showDelay={700} />
						<div className="title"
							data-pr-tooltip={subjectEditMode !== undefined ? undefined : determineChatHeader().toString()}
							data-pr-position="left">
							{determineChatHeader()}
						</div>
					</>
				)}
			</div>

			<div className="right-slot">
				{setVisible && exportPdfButton}
				{setVisible && selectedConversation && (
					<Button
						icon={subjectEditMode ? "pi pi-save" : "pi pi-pencil"}
						text
						className="conversation-header-btn edit-icon"
						onClick={() => onSubjectEditAction()}
					/>
				)}

				{((!isSelectedConversation && setVisible)) && (
					<>
						{(type === "ASKAI" && selectedConversation || !isSelectedConversation) && (<Button
							icon={filterVisible ? "pi pi-filter-slash" : "pi pi-filter"}
							text
							className="conversation-header-btn filter-icon"
							onClick={() => toggleFilterVisible()}
						/>)}

					</>
				)}

				{setVisible && (
					<>
						<Button
							icon="pi pi-times"
							text
							className="conversation-header-btn close-icon"
							onClick={() => setVisible(false)}
						/>
					</>
				)}
			</div>
		</>)
	}

	const determineChatboxPlaceholder = () => {
		if (type === "ASKAI" && needsCustomerForAskAi && newAskAiCustomer?.name) {
			return t("conversation.ask-ai.placeholder-for", { customer: newAskAiCustomer.name });
		}
		if (type === "ASKAI") {
			return isKnowledgeBaseAskAi ? t('knowledgeBase.askAi.placeholder') : t('conversation.ask-question')
		}
		return t("conversation.send-message");
	}

	useEffect(() => {
		const question = initialQuestion?.trim();
		if (!question || startedInitialQuestion.current) {
			return;
		}
		if (!conversationTypeSelectList?.some((item) => item.code === type)) {
			return;
		}
		startedInitialQuestion.current = true;
		setIsSelectedConversation(true);
		setChatDisabled(false);
		void (async () => {
			const conversation = await generateConversationBeforeChat(undefined, question);
			await submitConversationMessage(conversation, question);
		})();
	}, [initialQuestion, conversationTypeSelectList, type]);

	const chatbox = (
		<div className="conversation-chatbox">
			{needsCustomerForAskAi && (
				<div
					ref={composerCustomerRef}
					className={classNames(
						"conversation-compose-customer",
						selectedConversation
							? "conversation-compose-customer--chip"
							: "conversation-compose-customer--select",
						composerCustomerMissing && !newAskAiCustomer && "conversation-compose-customer--missing",
					)}
				>
					<span className="conversation-compose-customer__label">
						{t("conversation.ask-ai.composer.customer")}
					</span>
					{selectedConversation ? (
						<WorkflowCustomerTag
							customer={resolveCustomerByUuid(selectedConversation.customer?.uuid) ?? selectedConversation.customer ?? newAskAiCustomer}
							isMultiCustomer={isMultiCustomer}
						/>
					) : (
						<DashlyDropdown
							inputId="ask-ai-composer-customer"
							value={newAskAiCustomer}
							options={customerSelectItems}
							onChange={(event) => {
								setNewAskAiCustomer(event.value);
								setStoredAskAiCustomerUuid(event.value?.uuid ?? null);
								setComposerCustomerMissing(false);
							}}
							optionLabel="name"
							placeholder={t("user.customer")}
							filter
							controlSize="sm"
						/>
					)}
				</div>
			)}
			<div className="conversation-compose">
				<div className="conversation-compose__input" ref={composerInputWrapRef}>
					<InputTextarea
						placeholder={determineChatboxPlaceholder()}
						inputMode="text"
						rows={1}
						disabled={chatDisabled}
						value={chatMessage}
						onKeyDown={(event) => onChatEnterClicked(event)}
						onChange={(event) => onChatChange(event)}
						autoResize={true}
						autoFocus={isSidebarAskAi && !selectedConversation}
					/>
				</div>
				<Button
					icon="pi pi-send"
					disabled={chatDisabled}
					className="conversation-compose__send"
					onClick={(event) => onChatSubmit(event)}
					aria-label={t('conversation.send-message')}
				/>
			</div>
		</div>
	);

	return (
		<div className={classNames(
			"conversation-container",
			fullscreen ? "fullscreen" : "",
			embedded ? "embedded" : "",
			isSidebarAskAi && historyTrayOpen && "conversation-container--history-open",
		)}>
			<div className={classNames("conversation")}>

				{isSidebarAskAi && (
					<div className="conversation-mobile-bar">
						<Button
							type="button"
							icon={historyTrayOpen ? "pi pi-times" : "pi pi-bars"}
							rounded
							text
							className="conversation-mobile-bar__toggle"
							aria-expanded={historyTrayOpen}
							aria-controls={CONVERSATION_HISTORY_TRAY_ID}
							aria-label={historyTrayOpen
								? t("conversation.history-tray.close")
								: t("conversation.history-tray.open")}
							onClick={() => setHistoryTrayOpen((open) => !open)}
						/>
						<div className="conversation-mobile-bar__title">
							{subjectEditMode ? (
								<AutoComplete
									autoFocus
									value={subject}
									suggestions={autoCompleteSubjects}
									completeMethod={onSubjectCompleteMethod}
									delay={200}
									onChange={(event) => setSubject(event.value)}
								/>
							) : (
								<span>{selectedConversation?.title?.trim() || t("conversation.new-question")}</span>
							)}
						</div>
						{selectedConversation && (
							<Button
								type="button"
								icon={subjectEditMode ? "pi pi-save" : "pi pi-pencil"}
								text
								rounded
								className="conversation-header-btn edit-icon"
								aria-label={subjectEditMode ? t("common.action.save") : t("common.action.edit")}
								onClick={() => onSubjectEditAction()}
							/>
						)}
						<Button
							type="button"
							icon="pi pi-plus"
							text
							rounded
							className="conversation-header-btn conversation-mobile-bar__new"
							aria-label={t("conversation.new-question")}
							onClick={() => onAddConversationAction()}
						/>
					</div>
				)}

				<div className="conversation-header">
					{conversationHeader()}
				</div>

				<div className="conversation-body">
					{isSidebarAskAi && historyTrayOpen && (
						<button
							type="button"
							className="conversation-history-backdrop"
							aria-label={t("conversation.history-tray.close")}
							onClick={() => setHistoryTrayOpen(false)}
						/>
					)}
					{!fullscreen && (<div ref={filterRef} className={classNames("conversation-filter", filterVisible ? 'active' : '')}>
						{(filterVisible || !isSelectedConversation) && (
							<>
								{filterContent()}
							</>
						)}
					</div>)}

					<div className="conversation-content">
						<div className="grid grid-nogutter">
							{(fullscreen || !selectedConversation) && (
								isSidebarAskAi ? (
									<div
										id={CONVERSATION_HISTORY_TRAY_ID}
										className="conversation-history-tray"
									>
										{conversationTable()}
									</div>
								) : conversationTable()
							)}

							{(fullscreen || selectedConversation) && (
								<div className={classNames("conversation-chat", fullscreen ? "col-9" : "col-12")}>
									<div ref={chatContentRef} className="conversation-chatcontent">
										{!selectedConversation && (
											noConversationContent()
										)}

										{selectedConversation && (
											<>
												{conversationMessageContent()}
												<div ref={chatBottomRef} />
											</>
										)}
									</div>

									{fullscreen && chatbox}
								</div>
							)}
						</div>

						{!fullscreen && chatbox}

						{!fullscreen && <div ref={chatBottomRef} />}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Conversation